import React, { useState } from 'react';
import { Ban, ChevronDown, RotateCcw } from 'lucide-react';
import type { ServiceResa, ZoneResa } from '../../lib/reservationsApi';
import type { FermetureJour } from '../../lib/reservationsReglagesApi';
import { libelleCourt, libelleFermeture } from './fermetures';

/**
 * Restrictions du jour (L06-09, « Fermé en ce moment ») : UNE ligne discrète, qui se déplie à la demande. Fermé, on lit l'essentiel
 * (« Soir 20:00–21:00 fermé · Groupes de 2 dès 21:00 ») ; ouvert, une ligne par restriction avec son bouton « Rouvrir », utilisable à tout moment
 * (en plein service). Un service entier fermé se voit au point rouge ; chaque restriction est écrite en toutes lettres (jamais la couleur seule).
 */
interface Props {
    fermetures: FermetureJour[];
    services: ServiceResa[];
    zones: ZoneResa[];
    peutRouvrir: boolean;
    onRouvrir: (f: FermetureJour) => void;
}

export const FermesEnCeMoment: React.FC<Props> = ({ fermetures, services, zones, peutRouvrir, onRouvrir }) => {
    const [ouvert, setOuvert] = useState(false);
    if (fermetures.length === 0) return null;
    const complet = fermetures.some((f) => f.type === 'service');
    const courts = fermetures.map((f) => libelleCourt(f, services, zones));
    const resume = courts.slice(0, 2).join(' · ') + (courts.length > 2 ? ` · +${courts.length - 2}` : '');
    return (
        <section aria-label="Restrictions du jour" className="rounded-xl border border-border bg-surface-soft text-[13px]">
            <button type="button" aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)} className="w-full flex items-center gap-2.5 px-3.5 min-h-[44px] text-left">
                <span aria-hidden="true" className={`w-2 h-2 rounded-full shrink-0 ${complet ? 'bg-state-danger' : 'bg-state-warn'}`} />
                <Ban size={14} className="shrink-0 text-content-muted" aria-hidden="true" />
                <span className="font-semibold text-content shrink-0">{fermetures.length > 1 ? `${fermetures.length} restrictions` : '1 restriction'} aujourd'hui</span>
                <span className="text-content-muted truncate min-w-0">{resume}</span>
                <ChevronDown size={16} className={`ml-auto shrink-0 text-content-muted transition-transform ${ouvert ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {ouvert && (
                <ul className="border-t border-border px-3.5 py-2 divide-y divide-border-soft">
                    {fermetures.map((f) => (
                        <li key={f.id} className="flex items-center justify-between gap-3 py-1.5 min-h-[40px] max-lg:min-h-[44px]">
                            <span className="text-content">{libelleFermeture(f, services, zones)}</span>
                            {peutRouvrir && <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-semibold underline underline-offset-2 text-content min-h-[32px] max-lg:min-h-[44px] px-1" aria-label={`Rouvrir : ${libelleFermeture(f, services, zones)}`} onClick={() => onRouvrir(f)}><RotateCcw size={13} aria-hidden="true" />Rouvrir</button>}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};
